import { memo, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

import { GenericObject } from "../../../services/Price";
import MailsFormTemplate from "./MailsFormTemplate";
import AppButton from "../../../components/AppButton";
import { basicUpdate } from "../../../Configuration/utils";
import { deleteMail } from "./utils";
import { DateTime } from "luxon";

const MailsList = memo(({ mailsList }: { mailsList: GenericObject[] }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [currMailId, setCurrMailId] = useState<number | null>(null);

  const updateMailMutation = useMutation(
    ({
      url,
      data,
      options,
    }: {
      url: string;
      data: any;
      options?: GenericObject;
    }) => basicUpdate(url, data, options),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("notifications-mails-list");
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_SUCCESS" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_FAILED" }), {
          variant: "error",
        });
      },
      onSettled: () => {
        setCurrMailId(null);
      },
    }
  );

  const deleteBannerMutation = useMutation(
    (mailId: number) => deleteMail(mailId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("notifications-mails-list");
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_SUCCESS" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_FAILED" }), {
          variant: "error",
        });
      },
      onSettled: () => {
        setCurrMailId(null);
      },
    }
  );

  const handleUpdate = useCallback(
    ({ mailId, url, data }: { mailId: number; url: string; data: any }) => {
      setCurrMailId(mailId);
      updateMailMutation.mutate({ url, data });
    },
    [updateMailMutation]
  );

  const handleDelete = useCallback(
    (mailId: number) => {
      setCurrMailId(mailId);
      deleteBannerMutation.mutate(mailId);
    },
    [deleteBannerMutation]
  );

  const handleToggleMail = useCallback(
    (isActive: boolean, mailId: number) => {
      updateMailMutation.mutate({
        url: `/api/user/bloc-mails/${mailId}/`,
        data: { is_active: isActive },
        options: { method: "PATCH" },
      });
    },
    [updateMailMutation]
  );

  return (
    <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
      {mailsList.map((mail: GenericObject) => (
        <Box
          key={mail.id}
          sx={(theme) => ({
            border: `1px solid ${theme.vars.palette.border.inputOutlined}`,
            borderRadius: "6px",
            padding: 1,
            backgroundColor: "background.default",
          })}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Box>
              <Typography
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  color: "text.secondary",
                  marginRight: 0.5,
                }}
              >
                {intl.formatMessage({ id: "UPDATED_AT" })}
              </Typography>
              <Typography component="span">
                {DateTime.fromISO(mail.updated_at).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={mail.is_active}
                    onChange={(e) => {
                      handleToggleMail(e.target.checked, mail.id);
                    }}
                  />
                }
                labelPlacement="start"
                label={intl.formatMessage({
                  id: "NOTIFICATION_ACTIVATE_SINGLE_MAIL",
                })}
                sx={{ mr: 3 }}
              />
              <AppButton
                aria-label={intl.formatMessage({ id: "SAVE" })}
                form={`notification-mails-form-${mail.id}`}
                type="submit"
                loading={updateMailMutation.isLoading && currMailId === mail.id}
                startIcon={<SaveIcon />}
              >
                {intl.formatMessage({ id: "SAVE" })}
              </AppButton>
              <AppButton
                intent="danger"
                aria-label={intl.formatMessage({ id: "DELETE" })}
                onClick={() => handleDelete(mail.id)}
                loading={
                  deleteBannerMutation.isLoading && currMailId === mail.id
                }
                startIcon={<DeleteIcon />}
              >
                {intl.formatMessage({ id: "DELETE" })}
              </AppButton>
            </Box>
          </Box>
          <MailsFormTemplate mailData={mail} onUpdate={handleUpdate} />
        </Box>
      ))}
    </Box>
  );
});

export default MailsList;
